import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { CONTACT, NAV_LINKS, SERVICES } from "@/data/site";

export const Footer = () => (
  <footer className="bg-ink text-white relative grain overflow-hidden" data-testid="site-footer">
    <div className="blueprint-grid-dark absolute inset-0 pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3">
            <LogoMark className="h-10 w-10" />
            <span className="font-display font-extrabold text-xl tracking-tight">Tranzlytics</span>
          </div>
          <p className="mt-5 text-white/60 text-sm leading-relaxed max-w-sm">
            Architecting enterprise data pipelines and end-to-end IT infrastructure — from legacy SAS modernisation to Snowflake, Databricks, networking and hardware supply.
          </p>
          <Link
            to="/contact"
            data-testid="footer-cta-quote"
            className="mt-7 inline-flex items-center gap-2 bg-cobalt hover:bg-cobalt-hover text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors"
          >
            Start a Project
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="lg:col-span-2">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">Pages</p>
          <ul className="mt-5 space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} data-testid={`footer-${l.testid}`} className="text-sm text-white/70 hover:text-white transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-3">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">What we do</p>
          <ul className="mt-5 space-y-3">
            {SERVICES.map((s) => (
              <li key={s.id}>
                <Link to="/services" data-testid={`footer-service-${s.id}`} className="text-sm text-white/70 hover:text-white transition-colors">
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-2">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">Reach us</p>
          <ul className="mt-5 space-y-4 text-sm">
            <li>
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email-link" className="flex items-start gap-2.5 text-white/70 hover:text-white transition-colors break-all">
                <Mail className="h-4 w-4 mt-0.5 shrink-0" />
                {CONTACT.email}
              </a>
            </li>
            {CONTACT.phones.map((p, i) => (
              <li key={p}>
                <a href={CONTACT.phoneLinks[i]} data-testid={`footer-phone-link-${i}`} className="flex items-center gap-2.5 text-white/70 hover:text-white transition-colors">
                  <Phone className="h-4 w-4 shrink-0" />
                  {p}
                </a>
              </li>
            ))}
            <li className="flex items-start gap-2.5 text-white/70">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
              {CONTACT.address}
            </li>
          </ul>
        </div>
      </div>
      <div className="mt-16 select-none" aria-hidden="true">
        <p className="font-display font-extrabold tracking-tight leading-none text-[16vw] lg:text-[11rem] text-white/[0.05] whitespace-nowrap">
          Tranzlytics
        </p>
      </div>
      <div className="mt-6 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <p className="text-xs text-white/40" data-testid="footer-copyright">
          © {new Date().getFullYear()} Tranzlytics Services &amp; Technology. All rights reserved.
        </p>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/30">
          Data · Cloud · Network · Hardware
        </p>
      </div>
    </div>
  </footer>
);
